import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useListSpaces } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { Card } from '@cocoonspace/ui/components/card'
import { DialogHeader, DialogTitle } from '@cocoonspace/ui/components/dialog'
import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useLingui } from '@lingui/react/macro'
import { keepPreviousData } from '@tanstack/react-query'
import { TwicImg } from '@twicpics/components/react'
import { GripVerticalIcon } from 'lucide-react'

import { SpaceCapacity } from '~/domains/spaces/components/atoms/space-capacity'
import { SpaceSurface } from '~/domains/spaces/components/atoms/space-surface'
import type { LocationCocoonSchema } from '../../event-type-form'

const SpaceCard = ({ space, index }: { space: Space; index: number }) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: space.id })

	return (
		<div className='flex flex-row items-center gap-2'>
			<div className='text-sm font-bold'>{index + 1}.</div>
			<Card
				ref={setNodeRef}
				style={{
					transform: CSS.Transform.toString(transform),
					opacity: isDragging ? 0.5 : 1,
					transition,
				}}
				variant='outline'
				className='flex w-full flex-row items-center gap-4'
			>
				<div className='bg-muted/10 h-full w-24 overflow-hidden rounded-l'>
					<TwicImg
						src={space.images[0].path}
						alt={space.title}
						ratio='3/2'
						focus='50px100p'
						className='h-full w-full object-cover'
					/>
				</div>

				<div className='flex-1'>
					<div className='text-sm font-bold'>{space.title}</div>

					<div className='flex flex-row items-center gap-2 text-sm'>
						<SpaceCapacity space={space} />
						<SpaceSurface space={space} />
					</div>
				</div>

				<div
					className='text-muted-foreground flex size-12 cursor-grab touch-none items-center justify-center select-none'
					{...listeners}
					{...attributes}
				>
					<GripVerticalIcon />
				</div>
			</Card>
		</div>
	)
}

export const StepPriority = () => {
	const { t } = useLingui()

	const { watch, setValue } = useFormContext<LocationCocoonSchema>()
	const spaceIds = watch('space_ids')

	const sensors = useSensors(useSensor(PointerSensor))

	const { data: spacesByIds, isLoading: isLoadingSpaces } = useListSpaces(
		{ ids: spaceIds },
		{
			query: {
				placeholderData: keepPreviousData,
				select: ({ data }) => {
					return data.reduce(
						(acc, space) => {
							acc[space.id] = space
							return acc
						},
						{} as Record<string, Space>,
					)
				},
			},
		},
	)

	const spaces = useMemo(() => {
		return spaceIds.map((spaceId) => spacesByIds?.[spaceId])
	}, [spaceIds, spacesByIds])

	const onDragEnd = (event: DragEndEvent) => {
		const { active, over } = event

		if (over && active.id !== over.id) {
			const oldIndex = spaceIds.findIndex((i) => i === active.id)
			const newIndex = spaceIds.findIndex((i) => i === over.id)
			const newItems = [...spaceIds]
			const [moved] = newItems.splice(oldIndex, 1)

			newItems.splice(newIndex, 0, moved)

			setValue('space_ids', newItems, {
				shouldDirty: true,
				shouldValidate: true,
			})
		}
	}

	return (
		<>
			<DialogHeader className='p-6 pb-0'>
				<DialogTitle>{t`Donnez un ordre de priorité de réservation`}</DialogTitle>
			</DialogHeader>

			<div className='flex h-full w-full flex-1 flex-col'>
				<div className='px-6 pt-2 text-sm'>
					{t`Les créneaux seront proposés en fonction des disponibilités des Cocoons sélectionnés.`}
				</div>

				<div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
					<div>
						{isLoadingSpaces ? null : (
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={onDragEnd}
							>
								<SortableContext
									items={spaceIds}
									strategy={verticalListSortingStrategy}
								>
									<div className='flex w-full flex-col gap-2'>
										{spaces.map((space, index) => (
											<SpaceCard
												key={space?.id}
												index={index}
												space={space ?? ({} as Space)}
											/>
										))}
									</div>
								</SortableContext>
							</DndContext>
						)}
					</div>
				</div>
			</div>
		</>
	)
}
